/**
 * Fait : la partie QUI TRAVAILLE ICI de la fiche d'un projet — la liste des
 *   membres, leur niveau, la pastille de ceux qui sont là en ce moment, et
 *   depuis le lot 4 les trois gestes qui manquaient : changer le niveau de
 *   quelqu'un, le retirer, et QUITTER le projet soi-même.
 *
 *   Extraite de la fiche le 2026-08-23, quand celle-ci a dépassé le plafond de
 *   lignes du dépôt : c'est le morceau qui ne décide de rien et qui se lit
 *   seul, donc celui qui part le premier.
 *
 * LE RÉFÉRENT EST NOMMÉ, et avec ce mot-là (critère 133, `D-021`). Sans cette
 *   mention, un parent administrateur suivrait le projet sans qu'aucun
 *   participant ne le sache — c'est exactement ce que `D-021` interdit, et la
 *   migration de `D-026` en ouvre de vieux au fondateur. Le mot à l'écran est
 *   la seule chose qui rend cet accès déclaré plutôt que silencieux.
 *
 * POURQUOI LE RÉFÉRENT N'A PAS DE BOUTON « RETIRER » : le serveur le refuse
 *   toujours (`D-023` — un projet n'est jamais sans référent). Un bouton qui
 *   refuse à tous les coups est un bouton mort ; la mention « référent » dit ce
 *   qui le remplace. Quitter, en revanche, garde son bouton même pour un
 *   référent : le refus qu'il peut recevoir lui APPREND quoi faire à la place.
 *
 * Ne fait pas : elle n'appelle aucune API, ne décide d'aucun droit et ne sait
 *   pas d'où vient la présence — elle affiche ce qu'on lui donne, et remonte
 *   les gestes à la fiche.
 */

import type { NiveauDeMembre } from '@aymoai/shared';

import { Partie } from '../gabarits/Partie.tsx';
import type { MembreVu } from './api.ts';

/** Ce que chaque niveau permet, dit en français — jamais le mot de la base. */
const NOM_DU_NIVEAU: Readonly<Record<NiveauDeMembre, string>> = {
  lecture: 'Peut lire',
  modification: 'Peut lire et écrire',
};

export function SectionMembres({
  membres,
  presents,
  moiId,
  referentId,
  peutAdministrer,
  archive,
  surNiveau,
  surRetrait,
  surDepart,
}: {
  readonly membres: MembreVu[];

  /** Les identifiants de comptes présents à l'instant, tels que le canal les dit. */
  readonly presents: string[];

  /** Mon propre identifiant — pour reconnaître MA ligne, et elle seule. */
  readonly moiId: string;

  /** Le référent du projet (`D-023`), quand il y en a un. */
  readonly referentId?: string | undefined;

  readonly peutAdministrer: boolean;
  readonly archive: boolean;

  readonly surNiveau: (compteId: string, niveau: NiveauDeMembre) => void;
  readonly surRetrait: (compteId: string) => void;
  readonly surDepart: () => void;
}) {
  const maLigne = membres.find((membre) => membre.compteId === moiId);

  /*
   * CRITÈRE 134 : le propriétaire ne voit pas « Quitter ce projet ». Le serveur
   * le lui refuserait — il n'est pas un membre qu'on retire, il l'est par
   * construction — et le lui montrer ne lui offrirait qu'un refus trompeur.
   *
   * CRITÈRE 136 : quitter reste possible sur un projet ARCHIVÉ. L'archivage
   * gèle le remaniement, pas la liberté de partir ; sans cela, archiver
   * enfermerait les membres.
   */
  const peutQuitter = maLigne !== undefined && maLigne.niveau !== 'proprietaire';

  return (
    <Partie
      etiquette="Qui travaille ici"
      nature="gens"
      titre="Membres"
      aide="Qui a accès à ce projet, et avec quel droit."
      data-role="partie-membres"
    >
      <ul className="rangees" data-liste="membres">
        {membres.map((membre) => {
          const estLeReferent = referentId !== undefined && membre.compteId === referentId;
          const estMoi = membre.compteId === moiId;

          // On ne remanie pas le propriétaire : il n'est pas un droit qu'on
          // donne. Et un projet archivé se lit, il ne se remanie plus.
          const remaniable = peutAdministrer && !archive && membre.niveau !== 'proprietaire';

          return (
            <li key={membre.compteId} data-membre={membre.compteId} data-niveau={membre.niveau}>
              <strong>{membre.nomAffiche}</strong>
              <span className="texte-attenue">— {membre.niveau}</span>

              {estLeReferent && (
                <span className="texte-attenue" data-role="referent">
                  — référent de ce projet
                </span>
              )}

              {presents.includes(membre.compteId) ? (
                <span className="pastille-presence" data-role="present">
                  présent
                </span>
              ) : null}

              {remaniable && (
                /*
                 * `aria-label` PLUTÔT QU'UN LIBELLÉ MASQUÉ. Le produit met
                 * partout ailleurs un vrai `<label>` visible ; ici, la rangée
                 * porte déjà le nom de la personne juste avant, et un libellé
                 * de plus par ligne rendrait la liste illisible à l'œil. Le
                 * nom est REPRIS dans l'étiquette lue, pour qu'un lecteur
                 * d'écran ne dise jamais « liste déroulante » sans dire de qui.
                 */
                <select
                  aria-label={`Ce que ${membre.nomAffiche} peut faire`}
                  data-role="changer-le-niveau"
                  value={membre.niveau}
                  onChange={(evenement) =>
                    surNiveau(membre.compteId, evenement.target.value as NiveauDeMembre)
                  }
                >
                  <option value="lecture">{NOM_DU_NIVEAU.lecture}</option>
                  <option value="modification">{NOM_DU_NIVEAU.modification}</option>
                </select>
              )}

              {remaniable && !estMoi && !estLeReferent && (
                <button
                  type="button"
                  data-role="retirer-le-membre"
                  onClick={() => surRetrait(membre.compteId)}
                >
                  {`Retirer ${membre.nomAffiche}`}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {peutQuitter && (
        <p>
          <button type="button" data-role="quitter-le-projet" onClick={surDepart}>
            Quitter ce projet
          </button>
          <span className="texte-attenue">
            {' '}
            Vous n’y aurez plus accès. Il faudra qu’on vous le repartage.
          </span>
        </p>
      )}
    </Partie>
  );
}
